import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Lock as ResetIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const AdminUsersPage = () => {
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetDialog, setResetDialog] = useState({ open: false, userId: null, email: '', userName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [roleDialog, setRoleDialog] = useState({ open: false, userId: null, email: '', userName: '', currentRole: '', userType: '' });
  const [newRole, setNewRole] = useState('');
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleSuccess, setRoleSuccess] = useState('');
  const [statusDialog, setStatusDialog] = useState({ open: false, userId: null, email: '', userName: '', currentStatus: '', userType: '' });
  const [newStatus, setNewStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusSuccess, setStatusSuccess] = useState('');
  const [subscriptionDialog, setSubscriptionDialog] = useState({ open: false, userId: null, email: '', userName: '', currentSubscription: '', userType: '' });
  const [newSubscription, setNewSubscription] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState('');
  const [createUserDialog, setCreateUserDialog] = useState({ open: false });
  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    name: '',
    password: '',
    role: 'member',
    status: 'active'
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [filters, setFilters] = useState({
    roles: [],
    statuses: [],
    subscriptions: []
  });
  const [whitelist, setWhitelist] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [newWhitelistEmail, setNewWhitelistEmail] = useState('');
  const [newWhitelistNotes, setNewWhitelistNotes] = useState('');
  const [whitelistSuccess, setWhitelistSuccess] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [teamAssignDialog, setTeamAssignDialog] = useState({ open: false, userId: null, email: '', userName: '', currentTeam: '', userType: '' });
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teamAssignLoading, setTeamAssignLoading] = useState(false);
  const [teamAssignSuccess, setTeamAssignSuccess] = useState('');

  useEffect(() => {
    if (!isAdmin()) {
      setError('Nur Administratoren können diese Seite anzeigen');
      setLoading(false);
      return;
    }
    fetchAllUsers();
    fetchWhitelist();
    fetchAllTeams();
  }, [isAdmin]);

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/all-users');
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetClick = (userId, email, name, type) => {
    setResetDialog({
      open: true,
      userId,
      email,
      userName: name,
      type
    });
    setNewPassword('');
    setResetSuccess('');
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setResetLoading(true);
      await axios.post(`/api/admin/sub-users/${resetDialog.userId}/reset-password`, {
        newPassword
      });
      setResetSuccess(`Password reset for ${resetDialog.email}`);
      setResetDialog({ open: false, userId: null, email: '', userName: '' });
      setNewPassword('');
      setTimeout(() => setResetSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const handleRoleClick = (userId, email, name, currentRole, userType) => {
    setRoleDialog({
      open: true,
      userId,
      email,
      userName: name,
      currentRole,
      userType
    });
    setNewRole(currentRole);
    setRoleSuccess('');
  };

  const handleRoleChange = async () => {
    if (!newRole) {
      setError('Please select a role');
      return;
    }

    try {
      setRoleLoading(true);
      await axios.post(`/api/admin/users/${roleDialog.userId}/update-role`, {
        newRole,
        userType: roleDialog.userType
      });
      setRoleSuccess(`Role updated to ${getRoleLabel(newRole)} for ${roleDialog.email}`);
      setRoleDialog({ open: false, userId: null, email: '', userName: '', currentRole: '', userType: '' });
      setNewRole('');
      fetchAllUsers();
      setTimeout(() => setRoleSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setRoleLoading(false);
    }
  };

  const handleStatusClick = (userId, email, name, currentStatus, userType) => {
    setStatusDialog({
      open: true,
      userId,
      email,
      userName: name,
      currentStatus,
      userType
    });
    setNewStatus(currentStatus);
    setStatusSuccess('');
  };

  const handleStatusChange = async () => {
    if (!newStatus) {
      setError('Please select a status');
      return;
    }

    try {
      setStatusLoading(true);
      await axios.post(`/api/admin/users/${statusDialog.userId}/update-status`, {
        newStatus,
        userType: statusDialog.userType
      });
      setStatusSuccess(`Status updated to ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)} for ${statusDialog.email}`);
      setStatusDialog({ open: false, userId: null, email: '', userName: '', currentStatus: '', userType: '' });
      setNewStatus('');
      fetchAllUsers();
      setTimeout(() => setStatusSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubscriptionClick = (userId, email, name, currentSubscription, userType) => {
    setSubscriptionDialog({
      open: true,
      userId,
      email,
      userName: name,
      currentSubscription,
      userType
    });
    setNewSubscription(currentSubscription);
    setSubscriptionSuccess('');
  };

  const handleSubscriptionChange = async () => {
    if (!newSubscription) {
      setError('Please select a subscription');
      return;
    }

    try {
      setSubscriptionLoading(true);
      await axios.post(`/api/admin/users/${subscriptionDialog.userId}/update-subscription`, {
        newSubscription,
        userType: subscriptionDialog.userType
      });
      setSubscriptionSuccess(`Subscription updated to ${newSubscription} for ${subscriptionDialog.email}`);
      setSubscriptionDialog({ open: false, userId: null, email: '', userName: '', currentSubscription: '', userType: '' });
      setNewSubscription('');
      fetchAllUsers();
      setTimeout(() => setSubscriptionSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: '#9c27b0',
      member: '#2e7d32',
      viewer: '#1565c0'
    };
    return colors[role] || '#999';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      member: 'Marketingleiter',
      viewer: 'Betrachter'
    };
    return labels[role] || 'Unbekannt';
  };

  const getSubscriptionLabel = (subscription) => {
    const labels = {
      trial: 'Testversion',
      active: 'Aktiv',
      cancelled: 'Abgebrochen'
    };
    return labels[subscription] || subscription.charAt(0).toUpperCase() + subscription.slice(1);
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Aktiv',
      inactive: 'Inaktiv'
    };
    return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleCreateAdminUser = async () => {
    // Validate form
    if (!createUserForm.email || !createUserForm.name || !createUserForm.password) {
      setError('Bitte füllen Sie alle erforderlichen Felder aus');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createUserForm.email)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    // Validate password length
    if (createUserForm.password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      setCreateUserLoading(true);
      setError('');

      const response = await axios.post('/api/admin/create-user', {
        email: createUserForm.email.trim(),
        name: createUserForm.name.trim(),
        password: createUserForm.password,
        role: createUserForm.role,
        status: createUserForm.status
      });

      setError('');
      alert(`✅ Benutzer "${createUserForm.name}" (${createUserForm.role}) erfolgreich erstellt!`);
      setCreateUserDialog({ open: false });
      setCreateUserForm({ email: '', name: '', password: '', role: 'member', status: 'active' });
      fetchAllUsers();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Fehler beim Erstellen des Benutzers';
      setError(errorMsg);
      console.error('Error creating user:', err);
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      const currentFilters = prev[filterType] || [];
      if (currentFilters.includes(value)) {
        return {
          ...prev,
          [filterType]: currentFilters.filter(f => f !== value)
        };
      } else {
        return {
          ...prev,
          [filterType]: [...currentFilters, value]
        };
      }
    });
  };

  const filterUsers = (userList, userType) => {
    return userList.filter(user => {
      // Role filter
      if (filters.roles.length > 0 && !filters.roles.includes(user.role)) {
        return false;
      }

      // Status filter (email/password users only)
      if (userType === 'email-password' && filters.statuses.length > 0 && !filters.statuses.includes(user.status)) {
        return false;
      }

      // Subscription filter (Google OAuth users only)
      if (userType === 'google-oauth' && filters.subscriptions.length > 0 && !filters.subscriptions.includes(user.subscription || 'trial')) {
        return false;
      }

      return true;
    });
  };

  const fetchWhitelist = async () => {
    try {
      setWhitelistLoading(true);
      const response = await axios.get('/api/admin/google-whitelist');
      setWhitelist(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching whitelist:', err);
    } finally {
      setWhitelistLoading(false);
    }
  };

  const handleAddToWhitelist = async () => {
    if (!newWhitelistEmail) {
      setError('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newWhitelistEmail)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    try {
      setWhitelistLoading(true);
      await axios.post('/api/admin/google-whitelist', {
        email: newWhitelistEmail.trim(),
        notes: newWhitelistNotes.trim()
      });
      setWhitelistSuccess(`E-Mail "${newWhitelistEmail}" wurde zur Whitelist hinzugefügt`);
      setNewWhitelistEmail('');
      setNewWhitelistNotes('');
      fetchWhitelist();
      setTimeout(() => setWhitelistSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Hinzufügen zur Whitelist');
    } finally {
      setWhitelistLoading(false);
    }
  };

  const handleRemoveFromWhitelist = async (email) => {
    if (!window.confirm(`Möchten Sie "${email}" wirklich von der Whitelist entfernen?`)) {
      return;
    }

    try {
      setWhitelistLoading(true);
      await axios.delete(`/api/admin/google-whitelist/${encodeURIComponent(email)}`);
      setWhitelistSuccess(`E-Mail "${email}" wurde von der Whitelist entfernt`);
      fetchWhitelist();
      setTimeout(() => setWhitelistSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Entfernen von der Whitelist');
    } finally {
      setWhitelistLoading(false);
    }
  };

  const fetchAllTeams = async () => {
    try {
      setTeamsLoading(true);
      const response = await axios.get('/api/admin/all-teams');
      setTeams(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError(err.response?.data?.error || 'Fehler beim Laden der Teams');
    } finally {
      setTeamsLoading(false);
    }
  };

  const toggleTeamExpanded = (teamId) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
    }
    setExpandedTeams(newExpanded);
  };

  const handleTeamAssignClick = (userId, email, name, currentTeam, userType) => {
    setTeamAssignDialog({
      open: true,
      userId,
      email,
      userName: name,
      currentTeam: currentTeam || '',
      userType
    });
    setSelectedTeam(currentTeam || '');
    setTeamAssignSuccess('');
  };

  const handleTeamAssign = async () => {
    try {
      setTeamAssignLoading(true);
      await axios.post(`/api/admin/users/${teamAssignDialog.userId}/assign-team`, {
        teamId: selectedTeam || null,
        userType: teamAssignDialog.userType
      });
      const teamName = teams.find(t => t._id === selectedTeam)?.name || 'Removed';
      setTeamAssignSuccess(`Team assignment updated for ${teamAssignDialog.email}`);
      setTeamAssignDialog({ open: false, userId: null, email: '', userName: '', currentTeam: '', userType: '' });
      setSelectedTeam('');
      fetchAllUsers();
      setTimeout(() => setTeamAssignSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign team');
    } finally {
      setTeamAssignLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !users) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ m: 0 }}>
          Benutzer verwalten
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateUserDialog({ open: true })}
          sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45A049' } }}
        >
          Neuer Benutzer
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {resetSuccess && <Alert severity="success" sx={{ mb: 2 }}>{resetSuccess}</Alert>}
      {roleSuccess && <Alert severity="success" sx={{ mb: 2 }}>{roleSuccess}</Alert>}
      {statusSuccess && <Alert severity="success" sx={{ mb: 2 }}>{statusSuccess}</Alert>}
      {subscriptionSuccess && <Alert severity="success" sx={{ mb: 2 }}>{subscriptionSuccess}</Alert>}
      {teamAssignSuccess && <Alert severity="success" sx={{ mb: 2 }}>{teamAssignSuccess}</Alert>}

      {users && (
        <Box>
          {/* Summary */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Google OAuth-Konten
              </Typography>
              <Typography variant="h5">{users.summary.totalGoogleUsers}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="textSecondary">
                E-Mail/Passwort-Konten
              </Typography>
              <Typography variant="h5">{users.summary.totalEmailUsers}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Gesamt
              </Typography>
              <Typography variant="h5">{users.summary.totalUsers}</Typography>
            </Paper>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Filter
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3 }}>
              {/* Role Filter */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Rolle
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.roles.includes('admin')}
                        onChange={() => handleFilterChange('roles', 'admin')}
                      />
                    }
                    label="Administrator"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.roles.includes('member')}
                        onChange={() => handleFilterChange('roles', 'member')}
                      />
                    }
                    label="Marketingleiter"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.roles.includes('viewer')}
                        onChange={() => handleFilterChange('roles', 'viewer')}
                      />
                    }
                    label="Betrachter"
                  />
                </FormGroup>
              </Box>

              {/* Status Filter */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Status
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.statuses.includes('active')}
                        onChange={() => handleFilterChange('statuses', 'active')}
                      />
                    }
                    label="Aktiv"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.statuses.includes('inactive')}
                        onChange={() => handleFilterChange('statuses', 'inactive')}
                      />
                    }
                    label="Inaktiv"
                  />
                </FormGroup>
              </Box>

              {/* Subscription Filter */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Abonnement
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.subscriptions.includes('trial')}
                        onChange={() => handleFilterChange('subscriptions', 'trial')}
                      />
                    }
                    label="Testversion"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.subscriptions.includes('active')}
                        onChange={() => handleFilterChange('subscriptions', 'active')}
                      />
                    }
                    label="Aktiv"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.subscriptions.includes('cancelled')}
                        onChange={() => handleFilterChange('subscriptions', 'cancelled')}
                      />
                    }
                    label="Abgebrochen"
                  />
                </FormGroup>
              </Box>
            </Box>
          </Paper>

          {/* Google OAuth Users */}
          {users.googleOAuthUsers.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {(() => {
                const filteredGoogleUsers = filterUsers(users.googleOAuthUsers, 'google-oauth');
                return (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ m: 0 }}>
                        Google OAuth-Konten ({filteredGoogleUsers.length} von {users.googleOAuthUsers.length})
                      </Typography>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          borderRadius: '999px',
                          width: 'fit-content',
                          backgroundColor: '#c8e6c9',
                          color: '#2e7d32'
                        }}
                      >
                        Benutzer
                      </span>
                    </Box>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                            <TableCell>E-Mail</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Rolle</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell>Abonnement</TableCell>
                            <TableCell>Erstellt</TableCell>
                            <TableCell>Aktionen</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredGoogleUsers.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: 500,
                              borderRadius: '999px',
                              width: 'fit-content',
                              backgroundColor: user.role === 'admin' ? '#e1bee7' : user.role === 'member' ? '#c8e6c9' : '#bbdefb',
                              color: user.role === 'admin' ? '#6a1b9a' : user.role === 'member' ? '#2e7d32' : '#0d47a1'
                            }}
                          >
                            {getRoleLabel(user.role)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                borderRadius: '999px',
                                width: 'fit-content',
                                backgroundColor: user.teamName && user.teamName !== 'Keine team' ? '#c8e6c9' : '#ffcdd2',
                                color: user.teamName && user.teamName !== 'Keine team' ? '#2e7d32' : '#c62828'
                              }}
                            >
                              {user.teamName || 'Keine team'}
                            </span>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ minWidth: 'auto', p: 0.5, borderColor: '#4CAF50' }}
                              onClick={() => handleTeamAssignClick(user._id, user.email, user.name, user.teamName || '', 'google-oauth')}
                            >
                              <AddIcon sx={{ fontSize: '18px', color: '#4CAF50' }} />
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.subscription ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                borderRadius: '999px',
                                width: 'fit-content',
                                backgroundColor: user.subscription === 'active' ? '#c8e6c9' : user.subscription === 'trial' ? '#ffe0b2' : '#ffcdd2',
                                color: user.subscription === 'active' ? '#2e7d32' : user.subscription === 'trial' ? '#e65100' : '#c62828'
                              }}
                            >
                              {getSubscriptionLabel(user.subscription)}
                            </span>
                          ) : (
                            <span>N/A</span>
                          )}
                        </TableCell>
                        <TableCell>{new Date(user.createdAt).toLocaleDateString('de-DE')}</TableCell>
                        <TableCell sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleRoleClick(user._id, user.email, user.name, user.role, 'google-oauth')}
                            variant="outlined"
                          >
                            Rolle
                          </Button>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleSubscriptionClick(user._id, user.email, user.name, user.subscription || 'trial', 'google-oauth')}
                            variant="outlined"
                          >
                            Subscription
                          </Button>
                        </TableCell>
                      </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                );
              })()}
            </Box>
          )}

          {/* Email/Password Users */}
          {users.emailPasswordUsers.length > 0 && (
            <Box>
              {(() => {
                const filteredEmailUsers = filterUsers(users.emailPasswordUsers, 'email-password');
                return (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ m: 0 }}>
                        E-Mail/Passwort-Konten ({filteredEmailUsers.length} von {users.emailPasswordUsers.length})
                      </Typography>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          borderRadius: '999px',
                          width: 'fit-content',
                          backgroundColor: '#ffe0b2',
                          color: '#e65100'
                        }}
                      >
                        Subbenutzer
                      </span>
                    </Box>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                            <TableCell>E-Mail</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Rolle</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Erstellt</TableCell>
                            <TableCell>Aktionen</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredEmailUsers.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: 500,
                              borderRadius: '999px',
                              width: 'fit-content',
                              backgroundColor: user.role === 'admin' ? '#e1bee7' : user.role === 'member' ? '#c8e6c9' : '#bbdefb',
                              color: user.role === 'admin' ? '#6a1b9a' : user.role === 'member' ? '#2e7d32' : '#0d47a1'
                            }}
                          >
                            {getRoleLabel(user.role)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                borderRadius: '999px',
                                width: 'fit-content',
                                backgroundColor: user.teamName && user.teamName !== 'Keine team' ? '#c8e6c9' : '#ffcdd2',
                                color: user.teamName && user.teamName !== 'Keine team' ? '#2e7d32' : '#c62828'
                              }}
                            >
                              {user.teamName || 'Keine team'}
                            </span>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ minWidth: 'auto', p: 0.5, borderColor: '#4CAF50' }}
                              onClick={() => handleTeamAssignClick(user._id, user.email, user.name, user.teamName || '', 'email-password')}
                            >
                              <AddIcon sx={{ fontSize: '18px', color: '#4CAF50' }} />
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: 500,
                              borderRadius: '999px',
                              width: 'fit-content',
                              backgroundColor: user.status === 'active' ? '#c8e6c9' : '#ffcdd2',
                              color: user.status === 'active' ? '#2e7d32' : '#c62828'
                            }}
                          >
                            {getStatusLabel(user.status)}
                          </span>
                        </TableCell>
                        <TableCell>{new Date(user.createdAt).toLocaleDateString('de-DE')}</TableCell>
                        <TableCell sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleRoleClick(user._id, user.email, user.name, user.role, 'email-password')}
                            variant="outlined"
                          >
                            Rolle
                          </Button>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleStatusClick(user._id, user.email, user.name, user.status, 'email-password')}
                            variant="outlined"
                          >
                            Status
                          </Button>
                          <Button
                            size="small"
                            startIcon={<ResetIcon />}
                            onClick={() => handleResetClick(user._id, user.email, user.name, 'subuser')}
                            variant="outlined"
                          >
                            Passwort
                          </Button>
                        </TableCell>
                      </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                );
              })()}
            </Box>
          )}
        </Box>
      )}

      {/* Teams Section */}
      <Box sx={{ mt: 6, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Teams
        </Typography>

        {teamsLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : teams.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                  <TableCell width="50px"></TableCell>
                  <TableCell>Team Name</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Owner Email</TableCell>
                  <TableCell>Mitglieder</TableCell>
                  <TableCell>Erstellt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((team) => (
                  <React.Fragment key={team._id}>
                    <TableRow sx={{ '&:hover': { backgroundColor: theme.palette.grey[50] } }}>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => toggleTeamExpanded(team._id)}
                          sx={{ minWidth: 'auto' }}
                        >
                          {expandedTeams.has(team._id) ? '▼' : '▶'}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{team.name}</TableCell>
                      <TableCell>{team.ownerName}</TableCell>
                      <TableCell>{team.ownerEmail}</TableCell>
                      <TableCell>
                        <Chip
                          label={team.subUsers?.length || 0}
                          size="small"
                          variant="outlined"
                          sx={
                            (team.subUsers?.length || 0) > 0
                              ? { borderColor: '#4CAF50', color: '#4CAF50' }
                              : {}
                          }
                        />
                      </TableCell>
                      <TableCell>{new Date(team.createdAt).toLocaleDateString('de-DE')}</TableCell>
                    </TableRow>

                    {expandedTeams.has(team._id) && (
                      <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                        <TableCell colSpan={6} sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            Mitglieder ({team.subUsers?.length || 0})
                          </Typography>
                          {team.subUsers && team.subUsers.length > 0 ? (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
                                    <TableCell>E-Mail</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Rolle</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Erstellt</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {team.subUsers.map((subUser) => (
                                    <TableRow key={subUser._id}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                          <span>{subUser.email}</span>
                                          <span
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '6px',
                                              padding: '4px 12px',
                                              fontSize: '12px',
                                              fontWeight: 500,
                                              borderRadius: '999px',
                                              width: 'fit-content',
                                              backgroundColor: '#ffe0b2',
                                              color: '#e65100'
                                            }}
                                          >
                                            Subbenutzer
                                          </span>
                                        </Box>
                                      </TableCell>
                                      <TableCell>{subUser.name}</TableCell>
                                      <TableCell>
                                        <span
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '4px 12px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            borderRadius: '999px',
                                            width: 'fit-content',
                                            backgroundColor: subUser.role === 'admin' ? '#e1bee7' : subUser.role === 'member' ? '#c8e6c9' : '#bbdefb',
                                            color: subUser.role === 'admin' ? '#6a1b9a' : subUser.role === 'member' ? '#2e7d32' : '#0d47a1'
                                          }}
                                        >
                                          {getRoleLabel(subUser.role)}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <span
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '4px 12px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            borderRadius: '999px',
                                            width: 'fit-content',
                                            backgroundColor: subUser.status === 'active' ? '#c8e6c9' : '#ffcdd2',
                                            color: subUser.status === 'active' ? '#2e7d32' : '#c62828'
                                          }}
                                        >
                                          {getStatusLabel(subUser.status)}
                                        </span>
                                      </TableCell>
                                      <TableCell>{new Date(subUser.createdAt).toLocaleDateString('de-DE')}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              Keine Mitglieder in diesem Team
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Keine Teams gefunden
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Google Whitelist Management */}
      <Box sx={{ mt: 6, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Google Account Whitelist
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Nur E-Mail-Adressen in dieser Liste können sich mit ihrem Google-Konto anmelden
        </Typography>

        {/* Add to Whitelist Form */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            E-Mail zur Whitelist hinzufügen
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="E-Mail-Adresse"
              type="email"
              value={newWhitelistEmail}
              onChange={(e) => setNewWhitelistEmail(e.target.value)}
              placeholder="user@example.com"
              sx={{ flex: 1, minWidth: '250px' }}
            />
            <TextField
              label="Notizen (optional)"
              value={newWhitelistNotes}
              onChange={(e) => setNewWhitelistNotes(e.target.value)}
              placeholder="z.B. Kundennname"
              sx={{ flex: 1, minWidth: '250px' }}
            />
            <Button
              variant="contained"
              onClick={handleAddToWhitelist}
              disabled={whitelistLoading || !newWhitelistEmail}
              sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45A049' }, alignSelf: 'flex-end' }}
            >
              {whitelistLoading ? 'Wird hinzugefügt...' : 'Hinzufügen'}
            </Button>
          </Box>
        </Paper>

        {/* Whitelist Table */}
        {whitelist.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                  <TableCell>E-Mail</TableCell>
                  <TableCell>Hinzugefügt von</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Notizen</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {whitelist.map((entry) => (
                  <TableRow key={entry._id}>
                    <TableCell>{entry.email}</TableCell>
                    <TableCell>{entry.addedBy?.name || 'Admin'}</TableCell>
                    <TableCell>{new Date(entry.addedAt).toLocaleDateString('de-DE')}</TableCell>
                    <TableCell>{entry.notes || '-'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFromWhitelist(entry.email)}
                        disabled={whitelistLoading}
                      >
                        Entfernen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {whitelist.length === 0 && !whitelistLoading && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Noch keine E-Mails in der Whitelist
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onClose={() => setResetDialog({ ...resetDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Passwort zurücksetzen für {resetDialog.userName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Email: {resetDialog.email}
          </Typography>
          <TextField
            fullWidth
            label="Neues Passwort"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Mindestens 6 Zeichen"
            error={newPassword.length > 0 && newPassword.length < 6}
            helperText={newPassword.length > 0 && newPassword.length < 6 ? 'Mindestens 6 Zeichen erforderlich' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog({ ...resetDialog, open: false })}>
            Abbrechen
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            disabled={resetLoading || newPassword.length < 6}
          >
            {resetLoading ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialog.open} onClose={() => setRoleDialog({ ...roleDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Rolle ändern für {roleDialog.userName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Email: {roleDialog.email}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Neue Rolle</InputLabel>
            <Select
              value={newRole}
              label="Neue Rolle"
              onChange={(e) => setNewRole(e.target.value)}
            >
              <MenuItem value="admin">
                <Chip label="Administrator" size="small" variant="outlined" sx={{ color: '#9c27b0', borderColor: '#9c27b0' }} />
              </MenuItem>
              <MenuItem value="member">
                <Chip label="Marketingleiter" size="small" variant="outlined" sx={{ color: '#2e7d32', borderColor: '#2e7d32' }} />
              </MenuItem>
              <MenuItem value="viewer">
                <Chip label="Betrachter" size="small" variant="filled" sx={{ bgcolor: '#001a4d', color: 'white' }} />
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ ...roleDialog, open: false })}>
            Abbrechen
          </Button>
          <Button
            onClick={handleRoleChange}
            variant="contained"
            disabled={roleLoading || !newRole || newRole === roleDialog.currentRole}
          >
            {roleLoading ? 'Wird aktualisiert...' : 'Rolle ändern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ ...statusDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Status ändern für {statusDialog.userName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Email: {statusDialog.email}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Neuer Status</InputLabel>
            <Select
              value={newStatus}
              label="Neuer Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="active">
                <Chip label="Aktiv" size="small" color="success" />
              </MenuItem>
              <MenuItem value="inactive">
                <Chip label="Inaktiv" size="small" variant="outlined" color="error" />
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ ...statusDialog, open: false })}>
            Abbrechen
          </Button>
          <Button
            onClick={handleStatusChange}
            variant="contained"
            disabled={statusLoading || !newStatus || newStatus === statusDialog.currentStatus}
          >
            {statusLoading ? 'Wird aktualisiert...' : 'Status ändern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Subscription Dialog */}
      <Dialog open={subscriptionDialog.open} onClose={() => setSubscriptionDialog({ ...subscriptionDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Abonnement ändern für {subscriptionDialog.userName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Email: {subscriptionDialog.email}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Neues Abonnement</InputLabel>
            <Select
              value={newSubscription}
              label="Neues Abonnement"
              onChange={(e) => setNewSubscription(e.target.value)}
            >
              <MenuItem value="trial">
                <Chip label="Testversion" size="small" variant="outlined" color="warning" />
              </MenuItem>
              <MenuItem value="active">
                <Chip label="Aktiv" size="small" variant="outlined" color="success" />
              </MenuItem>
              <MenuItem value="cancelled">
                <Chip label="Abgebrochen" size="small" variant="outlined" color="error" />
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialog({ ...subscriptionDialog, open: false })}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSubscriptionChange}
            variant="contained"
            disabled={subscriptionLoading || !newSubscription || newSubscription === subscriptionDialog.currentSubscription}
          >
            {subscriptionLoading ? 'Wird aktualisiert...' : 'Abonnement ändern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Team Dialog */}
      <Dialog open={teamAssignDialog.open} onClose={() => setTeamAssignDialog({ ...teamAssignDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Team zuweisen für {teamAssignDialog.userName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Email: {teamAssignDialog.email}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Team auswählen</InputLabel>
            <Select
              value={selectedTeam}
              label="Team auswählen"
              onChange={(e) => setSelectedTeam(e.target.value)}
            >
              <MenuItem value="">
                <em style={{ color: '#f44336' }}>Keine team</em>
              </MenuItem>
              {teams.map((team) => (
                <MenuItem key={team._id} value={team._id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamAssignDialog({ ...teamAssignDialog, open: false })}>
            Abbrechen
          </Button>
          <Button
            onClick={handleTeamAssign}
            variant="contained"
            disabled={teamAssignLoading}
          >
            {teamAssignLoading ? 'Wird zugewiesen...' : 'Team zuweisen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog.open} onClose={() => setCreateUserDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Neuen Benutzer erstellen
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="E-Mail-Adresse"
              type="email"
              fullWidth
              value={createUserForm.email}
              onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
              placeholder="example@example.com"
              required
            />
            <TextField
              label="Vollständiger Name"
              fullWidth
              value={createUserForm.name}
              onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
              placeholder="Max Mustermann"
              required
            />
            <TextField
              label="Passwort"
              type="password"
              fullWidth
              value={createUserForm.password}
              onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
              placeholder="Mindestens 6 Zeichen"
              required
              helperText="Mindestens 6 Zeichen erforderlich"
            />
            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={createUserForm.role}
                onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                label="Rolle"
              >
                <MenuItem value="admin">
                  <Chip label="Administrator" size="small" sx={{ bgcolor: '#c41e3a', color: 'white' }} />
                </MenuItem>
                <MenuItem value="member">
                  <Chip label="Marketingleiter" size="small" sx={{ bgcolor: '#2e7d32', color: 'white' }} />
                </MenuItem>
                <MenuItem value="viewer">
                  <Chip label="Betrachter" size="small" sx={{ bgcolor: '#001a4d', color: 'white' }} />
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={createUserForm.status}
                onChange={(e) => setCreateUserForm({ ...createUserForm, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="active">
                  <Chip label="Aktiv" size="small" variant="outlined" color="success" />
                </MenuItem>
                <MenuItem value="inactive">
                  <Chip label="Inaktiv" size="small" variant="outlined" color="error" />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialog({ open: false })} disabled={createUserLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateAdminUser}
            variant="contained"
            disabled={createUserLoading || !createUserForm.email || !createUserForm.name || !createUserForm.password}
            sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45A049' } }}
          >
            {createUserLoading ? 'Wird erstellt...' : 'Benutzer erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsersPage;
